import DOMPurify from 'dompurify';

/**
 * HTML escrito por estudiantes (respuestas de actividades, publicaciones de la comunidad) antes de
 * pintarlo con dangerouslySetInnerHTML. El API también lo sanea al guardar, pero aquí se protege lo
 * que ya está en la base y cualquier ruta que no pase por ese filtro.
 */

const purify = DOMPurify(window);

const HTTP = /^https?:\/\//i;
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp);base64,/i;
const VIDEO_EMBED = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|player\.vimeo\.com)\//i;

// Lo que producen los editores (Quill en las entregas, CKEditor en la comunidad).
const TAGS = [
  'p', 'br', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'pre', 'code', 'a', 'img',
];
const COMMUNITY_TAGS = [...TAGS, 'figure', 'figcaption', 'oembed', 'table', 'thead', 'tbody', 'tr', 'th', 'td'];
const ATTRS = ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'class', 'style', 'spellcheck'];

purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    if (HTTP.test(node.getAttribute('href') || '')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    } else {
      node.removeAttribute('href');
    }
  }
  if (node.tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    if (!HTTP.test(src) && !DATA_IMAGE.test(src)) node.removeAttribute('src');
  }
});

export function sanitizeStudentHtml(html) {
  if (html === null || html === undefined) return '';
  return purify.sanitize(String(html), { ALLOWED_TAGS: TAGS, ALLOWED_ATTR: ATTRS });
}

/**
 * Publicación de la comunidad: se sanea y los videos embebidos (<oembed url>) se convierten en
 * iframes solo si son de YouTube o Vimeo por https.
 */
export function renderCommunityHtml(markup) {
  const fragment = purify.sanitize(String(markup ?? ''), {
    ALLOWED_TAGS: COMMUNITY_TAGS,
    ALLOWED_ATTR: [...ATTRS, 'url'],
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll('oembed').forEach((embed) => {
    const url = (embed.getAttribute('url') || '').replace('watch?v=', 'embed/');
    if (!VIDEO_EMBED.test(url)) {
      embed.remove();
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.setAttribute('width', 'auto');
    iframe.setAttribute('height', 'auto');
    iframe.setAttribute('allowFullScreen', '');
    iframe.setAttribute('frameBorder', '0');
    iframe.setAttribute('src', url);
    embed.replaceWith(iframe);
  });
  const container = document.createElement('span');
  container.appendChild(fragment);
  return container.outerHTML;
}
